import React, { useState, useEffect, type SetStateAction } from 'react'
import Button from './Button'
import Board from './Board'
import HintSection from './HintContainer'
import generateGameData from './gameGeneration'
import PokeBall from './PokeBall'
import Lives from './Lives'
import GameOver from './GameOver'
import GameWon from './GameWon'
import styled from 'styled-components'
import type { Solution, Pokemon, Hint } from './types'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  width: 100%;
  padding: 2rem;
  box-sizing: border-box;
`

const StyledGame = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr) .5fr;
  grid-template-rows: repeat(4, 1fr) .5fr;
  grid-column-gap: 1rem;
  grid-row-gap: 1rem;

  @media (max-width: 768px) {
    grid-template-rows: 1fr;
    grid-template-columns: 1fr;
  }
`

const ButtonSet = styled.div`
  grid-area: 5 / 2 / 6 / 5;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin: 0;
  padding: 0;
  gap: 1rem;

  @media (max-width: 480px) {
    grid-area: 2;
  }
`

const NUMBER_OF_LIVES = 6

const Loading = (): JSX.Element => {
  return (
    <PokeBall color='red' animation='rotate' />
  )
}

interface ActiveGameProps {
  solution: Solution | null
  isLoading: boolean
  selectedPokemon: Pokemon[]
  setSelectedPokemon: React.Dispatch<React.SetStateAction<Pokemon[]>>
  unsolvedPokemon: Pokemon[]
  setUnsolvedPokemon: React.Dispatch<React.SetStateAction<Pokemon[]>>
  solvedGroups: string[]
  setSolvedGroups: React.Dispatch<React.SetStateAction<string[]>>
  hints: Hint[]
  setHints: React.Dispatch<React.SetStateAction<Hint[]>>
  isSubmitted: boolean
  setIsSubmitted: React.Dispatch<React.SetStateAction<boolean>>
  lives: number
  setLives: React.Dispatch<React.SetStateAction<number>>
}

const ActiveGame = ({ unsolvedPokemon, selectedPokemon, setSelectedPokemon, solvedGroups, isSubmitted, hints, lives, setIsSubmitted, solution, setUnsolvedPokemon, setSolvedGroups, setLives }: ActiveGameProps): JSX.Element => {
  const triggerShake = (): void => {
    setIsSubmitted(true)
    setTimeout(() => { setIsSubmitted(false) }, 400)
  }

  function handleSubmit (): void {
    if (selectedPokemon.length === 4) {
      const solved = solution?.typeGroup.pokemonGroup.every(pokemon => selectedPokemon.includes(pokemon))
      if (solved !== undefined && solved) {
        setUnsolvedPokemon(unsolvedPokemon.filter(pokemon => !selectedPokemon.includes(pokemon)))
        setSolvedGroups([...solvedGroups, `type: ${solution?.typeGroup.type}`])
      }
      const solved2 = solution?.generationGroup.pokemonGroup.every(pokemon => selectedPokemon.includes(pokemon))
      if (solved2 !== undefined && solved2) {
        setUnsolvedPokemon(unsolvedPokemon.filter(pokemon => !selectedPokemon.includes(pokemon)))
        setSolvedGroups([...solvedGroups, `generation: ${solution?.generationGroup.generation}`])
      }
      const solved3 = solution?.abilityGroup.pokemonGroup.every(pokemon => selectedPokemon.includes(pokemon))
      if (solved3 !== undefined && solved3) {
        setUnsolvedPokemon(unsolvedPokemon.filter(pokemon => !selectedPokemon.includes(pokemon)))
        setSolvedGroups([...solvedGroups, `ability: ${solution?.abilityGroup.ability}`])
      }
      const solved4 = solution?.moveGroup.pokemonGroup.every(pokemon => selectedPokemon.includes(pokemon))
      if (solved4 !== undefined && solved4) {
        setUnsolvedPokemon(unsolvedPokemon.filter(pokemon => !selectedPokemon.includes(pokemon)))
        setSolvedGroups([...solvedGroups, `move: ${solution?.moveGroup.move}`])
      }

      if (
        (solved !== undefined && solved) ||
          (solved2 !== undefined && solved2) ||
          (solved3 !== undefined && solved3) ||
          (solved4 !== undefined && solved4)) {
        setSelectedPokemon([])
      } else {
        triggerShake()
        setLives(lives - 1)
      }
    }
  }

  function handleUnselect (): void {
    setSelectedPokemon([])
  }

  return (
    <StyledGame>
        <Board unsolvedPokemon={unsolvedPokemon}
        selectedPokemon={selectedPokemon}
        setSelectedPokemon={setSelectedPokemon}
        solvedGroups={solvedGroups}
        isSubmitted={isSubmitted} />
      <HintSection hints={hints} />
      <ButtonSet>
        <Button onClick={() => { handleSubmit() }} text="Submit" />
        <Button onClick={() => { handleUnselect() }} text="Unselect" />
      </ButtonSet>
      <Lives lives={NUMBER_OF_LIVES} remainingLives={lives} />
    </StyledGame>
  )
}

function Game (): JSX.Element {
  const [solution, setSolution] = useState<Solution | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedPokemon, setSelectedPokemon] = useState<Pokemon[]>([])
  const [unsolvedPokemon, setUnsolvedPokemon] = useState<Pokemon[]>([])
  const [solvedGroups, setSolvedGroups] = useState<string[]>([])
  const [hints, setHints] = useState<Hint[] | []>([])
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [lives, setLives] = useState(NUMBER_OF_LIVES)

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      await handleGenerateGameData()
    }
    void fetchData()
  }, [])

  useEffect(() => {
    if (solution != null) {
      const randomize = (array: any[]): any[] => {
        return array.sort(() => Math.random() - 0.5)
      }
      setUnsolvedPokemon(randomize([...solution.typeGroup.pokemonGroup, ...solution.generationGroup.pokemonGroup, ...solution.abilityGroup.pokemonGroup, ...solution.moveGroup.pokemonGroup]))
      setHints([{ group: 'type', answer: solution.typeGroup.type }, { group: 'generation', answer: solution.generationGroup.generation }, { group: 'ability', answer: solution.abilityGroup.ability }, { group: 'move', answer: solution.moveGroup.move }])
      console.log(solution)
    }
  }, [solution])

  async function handleGenerateGameData (): Promise<void> {
    setIsLoading(true)
    setSolution(await generateGameData() as SetStateAction<Solution | null>)
    setIsLoading(false)
  }

  return (
    <Wrapper>
      { isLoading
        ? <Loading />
        : lives > 0 && unsolvedPokemon.length > 0
          ? <ActiveGame solution={solution} isLoading={isLoading} selectedPokemon={selectedPokemon} setSelectedPokemon={setSelectedPokemon} unsolvedPokemon={unsolvedPokemon} setUnsolvedPokemon={setUnsolvedPokemon} solvedGroups={solvedGroups} setSolvedGroups={setSolvedGroups} hints={hints} setHints={setHints} isSubmitted={isSubmitted} setIsSubmitted={setIsSubmitted} lives={lives} setLives={setLives} />
          : lives === 0
            ? <GameOver />
            : <GameWon />
      }
    </Wrapper>
  )
}

export default Game
